import { Injectable } from '@angular/core';
import { ColumnDataTypes } from '@database/models';
import { ColumnRepository, DataRepository } from '@database/workspace';
import { DataByTableTypeProvider } from '@domain/business-layer';
import { ColumnModel, DataModel, fileBooleanMappings, getPrimaryKeys, PrimaryKeyValueModel } from '@shared';
import { combineLatest, map, Observable, switchMap } from 'rxjs';
import { SearchState } from './search.state';

@Injectable()
export class SearchService {
	public constructor(
		private dataRepository: DataRepository,
		private dataByTableTypeProvider: DataByTableTypeProvider,
		private columsRepository: ColumnRepository,
		private searchState: SearchState
	) {}

	public getSearchResults$(
		tableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[]
	): Observable<DataModel[]> {
		return this.searchState
			.get(tableName)
			.pipe(
				switchMap((searchKeywords: string | null) =>
					this.columsRepository
						.getColumns$(tableName)
						.pipe(
							switchMap((columns: ColumnModel[]) =>
								this.dataRepository
									.getChildTableData$(tableName, projectNumber, primaryKeyValue)
									.pipe(
										map((tableData: DataModel[]) =>
											this.tableDataSearchResults(
												tableData,
												columns,
												searchKeywords,
												getPrimaryKeys(primaryKeyValue)
											)
										)
									)
							)
						)
				)
			);
	}

	public getTableData$(tableName: string | null, projectNumber: string): Observable<DataModel[]> {
		return this.searchState
			.get(tableName)
			.pipe(
				switchMap((searchKeywords: string | null) =>
					this.columsRepository
						.getColumns$(tableName)
						.pipe(
							switchMap((columns: ColumnModel[]) =>
								this.dataRepository
									.getTableData$(tableName, projectNumber)
									.pipe(
										map((tableData: DataModel[]) =>
											this.tableDataSearchResults(tableData, columns, searchKeywords)
										)
									)
							)
						)
				)
			);
	}

	public getCombinedSortedData$(
		tableName: string,
		standardTableName: string,
		projectNumber: string,
		primaryKeyValue?: PrimaryKeyValueModel[]
	): Observable<[DataModel[], DataModel[]]> {
		return this.searchState
			.get(tableName)
			.pipe(
				switchMap((searchKeywords: string | null) =>
					combineLatest([
						this.columsRepository.getColumns$(tableName),
						this.columsRepository.getColumns$(standardTableName)
					]).pipe(
						switchMap(([tableColumns, standardTableColumns]: [ColumnModel[], ColumnModel[]]) =>
							this.dataByTableTypeProvider
								.getCombinedSampleData(tableName, standardTableName, projectNumber, primaryKeyValue)
								.pipe(
									map(
										([tableData, standardTableData]: [DataModel[], DataModel[]]) =>
											[
												this.tableDataSearchResults(
													tableData,
													tableColumns,
													searchKeywords,
													getPrimaryKeys(primaryKeyValue)
												),
												this.tableDataSearchResults(
													standardTableData,
													standardTableColumns,
													searchKeywords,
													getPrimaryKeys(primaryKeyValue)
												)
											] as [DataModel[], DataModel[]]
									)
								)
						)
					)
				)
			);
	}

	private tableDataSearchResults(
		tableData: DataModel[],
		columns: ColumnModel[],
		searchKeywords: string,
		columnsToSkip?: string[]
	): DataModel[] {
		columns = columns.filter((dataColumn: ColumnModel) => fileBooleanMappings[dataColumn.display_column]);

		if (columnsToSkip) {
			columns = columns.filter((dataColumn: ColumnModel) =>
				columnsToSkip.some((columnName: string) => columnName !== dataColumn.column_name)
			);
		}

		if (!searchKeywords) {
			return tableData;
		}

		return tableData.filter((tableDataItem: DataModel) => {
			for (const column of columns) {
				if (column.data_type === ColumnDataTypes.CHAR && tableDataItem[column.column_name]) {
					const tableDataFound: boolean = tableDataItem[column.column_name]
						.toLowerCase()
						.includes(searchKeywords.toLowerCase());

					if (tableDataFound) {
						return true;
					}
				}

				if (column.data_type === ColumnDataTypes.NUMERIC && tableDataItem[column.column_name] !== undefined) {
					const tableDataFound: boolean = tableDataItem[column.column_name]
						.toString()
						.includes(searchKeywords.toLowerCase());

					if (tableDataFound) {
						return true;
					}
				}
			}

			return false;
		});
	}
}
